/**
 * StreetViewModal Component
 * 
 * Modal to preview Street View imagery for locations
 * Preview turns, gates, and well site access before arriving
 * 
 * Features:
 * - Check if Street View is available at location
 * - Display street-level imagery
 * - Rotate view (N/E/S/W)
 * - Adjust pitch (up/down)
 * - Full panorama mode
 */

import { useState, useEffect, useCallback } from 'react';

interface StreetViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  lat: number;
  lng: number;
  title?: string;
  radius?: number;
}

interface StreetViewData {
  available: boolean;
  location?: {
    lat: number;
    lng: number;
  };
  panoId?: string;
  date?: string;
  copyright?: string;
  imageUrl?: string;
  thumbnailUrl?: string;
}

interface PanoramaData {
  available: boolean;
  location?: {
    lat: number;
    lng: number;
  };
  date?: string;
  panorama?: Array<{
    heading: number;
    url: string;
  }>;
}

export function StreetViewModal({ 
  isOpen, 
  onClose, 
  lat, 
  lng, 
  title = 'Street View Preview',
  radius = 50 
}: StreetViewModalProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<StreetViewData | null>(null);
  const [heading, setHeading] = useState(0);
  const [pitch, setPitch] = useState(0);
  const [showPanorama, setShowPanorama] = useState(false);
  const [panoramaData, setPanoramaData] = useState<PanoramaData | null>(null);

  // Direction labels
  const directionLabels: Record<number, string> = {
    0: 'North',
    90: 'East',
    180: 'South',
    270: 'West',
  };

  // Fetch Street View metadata
  const fetchStreetView = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        lat: lat.toString(),
        lng: lng.toString(),
        mode: 'metadata',
        heading: heading.toString(),
        pitch: pitch.toString(),
        radius: radius.toString(),
        size: '600x400',
      });

      const response = await fetch(`/api/google/streetview?${params}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch Street View data');
      }

      const result: StreetViewData = await response.json();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load Street View');
    } finally {
      setLoading(false);
    }
  }, [lat, lng, heading, pitch, radius]);

  // Fetch panorama data
  const fetchPanorama = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        lat: lat.toString(),
        lng: lng.toString(),
        mode: 'panorama',
        radius: radius.toString(),
      });

      const response = await fetch(`/api/google/streetview?${params}`);
      
      if (response.ok) {
        const result: PanoramaData = await response.json();
        setPanoramaData(result);
      }
    } catch (err) {
      console.error('Failed to fetch panorama:', err);
    }
  }, [lat, lng, radius]);

  // Fetch data when modal opens or heading/pitch changes
  useEffect(() => {
    if (isOpen) {
      fetchStreetView();
    }
  }, [isOpen, fetchStreetView]);

  // Fetch panorama data when toggled
  useEffect(() => {
    if (isOpen && showPanorama && !panoramaData) {
      fetchPanorama();
    }
  }, [isOpen, showPanorama, panoramaData, fetchPanorama]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setHeading(0);
      setPitch(0);
      setShowPanorama(false);
      setPanoramaData(null);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  // Rotate view
  const rotateLeft = () => {
    setHeading((prev) => (prev - 90 + 360) % 360);
  };

  const rotateRight = () => {
    setHeading((prev) => (prev + 90) % 360);
  };

  // Adjust pitch
  const pitchUp = () => {
    setPitch((prev) => Math.min(prev + 15, 45));
  };

  const pitchDown = () => {
    setPitch((prev) => Math.max(prev - 15, -45));
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)' }}
      onClick={onClose}
    >
      <div 
        className="bg-gray-900 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            <h2 className="text-lg font-semibold text-white">{title}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-full transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {loading && (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center justify-center h-64 text-red-400">
              <svg className="w-12 h-12 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && data && (
            <>
              {data.available ? (
                <div>
                  {/* Single view mode */}
                  {!showPanorama && (
                    <div className="relative">
                      <img
                        src={`/api/google/streetview?mode=image&lat=${lat}&lng=${lng}&heading=${heading}&pitch=${pitch}&radius=${radius}&size=800x500`}
                        alt="Street View"
                        className="w-full rounded-lg"
                        style={{ maxHeight: '400px', objectFit: 'cover' }}
                      />
                      
                      {/* Direction indicator */}
                      <div className="absolute top-2 left-2 bg-black/70 px-3 py-1 rounded-full text-white text-sm">
                        {directionLabels[heading] || `${heading}°`}
                      </div>

                      {/* Controls overlay */}
                      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                        <button
                          onClick={rotateLeft}
                          className="p-2 bg-black/70 hover:bg-black/90 text-white rounded-full transition-colors"
                          title="Rotate Left"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                        <button
                          onClick={pitchUp}
                          className="p-2 bg-black/70 hover:bg-black/90 text-white rounded-full transition-colors"
                          title="Look Up"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                        </button>
                        <button
                          onClick={pitchDown}
                          className="p-2 bg-black/70 hover:bg-black/90 text-white rounded-full transition-colors"
                          title="Look Down"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        <button
                          onClick={rotateRight}
                          className="p-2 bg-black/70 hover:bg-black/90 text-white rounded-full transition-colors"
                          title="Rotate Right"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Panorama mode */}
                  {showPanorama && panoramaData?.panorama && (
                    <div className="grid grid-cols-2 gap-2">
                      {panoramaData.panorama.map((view) => (
                        <div key={view.heading} className="relative">
                          <img
                            src={`/api/google/streetview?mode=image&lat=${lat}&lng=${lng}&heading=${view.heading}&pitch=0&radius=${radius}&size=400x300`}
                            alt={`View ${directionLabels[view.heading]}`}
                            className="w-full rounded-lg"
                          />
                          <div className="absolute bottom-2 left-2 bg-black/70 px-2 py-1 rounded text-white text-xs">
                            {directionLabels[view.heading]}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Metadata */}
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-4 text-sm text-gray-400">
                    <div className="flex items-center gap-4">
                      {data.date && (
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {data.date}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {lat.toFixed(5)}, {lng.toFixed(5)}
                      </span>
                    </div>
                    <button
                      onClick={() => setShowPanorama(!showPanorama)}
                      className="flex items-center gap-1 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                    >
                      {showPanorama ? (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          Single View
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                          </svg>
                          360° View
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                  <svg className="w-16 h-16 mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  <p className="text-lg mb-2">No Street View Available</p>
                  <p className="text-sm text-gray-500">
                    Street View imagery is not available at this location.
                  </p>
                  <p className="text-xs text-gray-600 mt-2">
                    Common for remote oilfield locations. Try nearby roads.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-700 bg-gray-800/50">
          <div className="text-xs text-gray-500">
            {data?.copyright || '© Google'}
          </div>
          <div className="flex gap-2">
            {data?.available && (
              <a
                href={`https://www.google.com/maps/@${lat},${lng},3a,75y,${heading}h,${90 - pitch}t/data=!3m1!1e3`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 text-sm text-blue-400 hover:text-blue-300 hover:bg-gray-700 rounded transition-colors"
              >
                Open in Google Maps
              </a>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StreetViewModal;
